import { useMemo, useState, useEffect, useRef } from "react";
import { proxyHandler_undefined } from "../helpers";
import { useLocalizedText } from "./useLocalizedText";

const DEFAULT_DEBOUNCE_MS = 500;

/**
 * Client-side search + pagination over `data`.
 * @param {unknown[]} data
 * @param {number} [debounceMs=500] — debounce delay for filter (input updates immediately).
 */
export const useClientSearch = (data, debounceMs = DEFAULT_DEBOUNCE_MS) => {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const { getLocalizedNumber } = useLocalizedText();
  const per_page = 10;

  useEffect(() => {
    if (debounceMs <= 0) {
      setDebouncedSearch(searchInput);
      return;
    }
    const id = setTimeout(() => setDebouncedSearch(searchInput), debounceMs);
    return () => clearTimeout(id);
  }, [searchInput, debounceMs]);

  const mapFunctionality = new Proxy(
    {
      function: () => "",
      string: (value) => value,
      number: (value) => String(value),
      boolean: () => "",
      object: (obj) =>
        Object.values(obj)
          .map((v) => [v, getLocalizedNumber(v)].join(","))
          .join(","),
      "[Object Object]": (obj) =>
        Object.values(obj)
          .map((v) => [v, getLocalizedNumber(v)].join(","))
          .join(","),
    },
    proxyHandler_undefined("")
  );

  const filtered = useMemo(() => {
    return data.filter((city) => {
      return mapFunctionality[typeof city](city)
        ?.toLowerCase()
        .includes(debouncedSearch.toLowerCase());
    });
  }, [debouncedSearch, data]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / per_page));
  const prevDebouncedRef = useRef(debouncedSearch);

  useEffect(() => {
    const searchChanged = prevDebouncedRef.current !== debouncedSearch;
    prevDebouncedRef.current = debouncedSearch;
    if (searchChanged) {
      setCurrentPage(1);
      return;
    }
    setCurrentPage((p) => Math.min(p, totalPages));
  }, [debouncedSearch, totalPages]);

  const filteredData = useMemo(() => {
    const start = (currentPage - 1) * per_page;
    return filtered.slice(start, start + per_page);
  }, [filtered, currentPage, per_page]);

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  return {
    searchValue: searchInput,
    filteredData,
    onSearchChange: handleSearchChange,
    pagination: {
      current: currentPage,
      total: filtered.length,
      pageSize: per_page,
      onChange: (page) => setCurrentPage(page),
    },
  };
};
